import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section } from "../../components/Common";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Power, KeyRound, Pencil, ShieldPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useDataSync } from "../../lib/realtime";
import EditUserModal from "./EditUserModal";

const ROLES = ["all", "admin", "teacher", "student", "parent"];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "" });
  const [inviting, setInviting] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "student", phone: "",
    batch_ids: [], parent_of: "",
  });

  const load = async () => {
    const [u, b] = await Promise.all([api.get("/admin/users"), api.get("/admin/batches")]);
    setUsers(u.data);
    setBatches(b.data);
  };
  useEffect(() => { load(); }, []);
  useDataSync(["users", "batches"], () => load());

  const filtered = filter === "all" ? users : users.filter((u) => u.role === filter);
  const students = users.filter((u) => u.role === "student");

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.parent_of) delete payload.parent_of;
      if (!payload.phone) delete payload.phone;
      await api.post("/admin/users", payload);
      toast.success("User created");
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "student", phone: "", batch_ids: [], parent_of: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/admin/invite-admin", invite);
      if (data.mocked && data.invite_link) {
        toast.info(`Email mocked — share this link manually: ${data.invite_link}`, { duration: 15000 });
      } else {
        toast.success(`Admin invite sent to ${invite.email}`);
      }
      setShowInvite(false);
      setInvite({ name: "", email: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      const url = u.is_active === false ? `/admin/users/${u.id}/activate` : `/admin/users/${u.id}/deactivate`;
      await api.post(url);
      toast.success(u.is_active === false ? "Activated" : "Deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetPw = async (u) => {
    const choice = window.prompt(`Password reset for ${u.name}\n\nType a new password to set it directly, OR leave blank and click OK to email them a reset link.`, "");
    if (choice === null) return;
    try {
      const body = choice ? { new_password: choice } : {};
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, body);
      if (data.mode === "set") {
        toast.success("Password updated");
      } else if (data.reset_link) {
        window.prompt("Email was mocked — copy this reset link and share it with the user:", data.reset_link);
      } else {
        toast.success("Reset link emailed");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Add students, teachers, parents, and admins."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => { setShowInvite((v) => !v); setShowForm(false); }}
              data-testid="btn-invite-admin"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <ShieldPlus className="h-4 w-4" /> Invite admin
            </button>
            <button
              onClick={() => { setShowForm((v) => !v); setShowInvite(false); }}
              data-testid="btn-add-user"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> Add user
            </button>
          </div>
        }
      />

      {showInvite && (
        <Section title="Invite a new admin">
          <form onSubmit={submitInvite} className="grid sm:grid-cols-2 gap-4" data-testid="invite-admin-form">
            <div>
              <label className="text-sm font-semibold text-slate-700">Full name</label>
              <input required value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                data-testid="invite-admin-name"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Email</label>
              <input type="email" required value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                data-testid="invite-admin-email"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2 text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg p-3">
              They will receive an email with a set-password link (valid 24 hours). After setting the password, they sign in at the private <b>/admin-login</b> page with email OTP verification — just like you.
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" disabled={inviting} data-testid="invite-admin-submit" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
                {inviting ? "Sending..." : "Send invite"}
              </button>
              <button type="button" onClick={() => setShowInvite(false)} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </Section>
      )}

      {showForm && (
        <Section title="Create user">
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="user-create-form">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="user-name" required />
            <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="user-email" required />
            <Input label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="user-password" required />
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="user-phone" />
            <div>
              <label className="text-sm font-semibold text-slate-700">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="user-role"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none"
              >
                {["admin", "teacher", "student", "parent"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {(form.role === "student" || form.role === "teacher") && (
              <div>
                <label className="text-sm font-semibold text-slate-700">Batches</label>
                <div className="mt-1.5 max-h-32 overflow-auto p-2 rounded-lg border border-slate-200 space-y-1">
                  {batches.length === 0 && <p className="text-xs text-slate-500">No batches yet.</p>}
                  {batches.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.batch_ids.includes(b.id)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...form.batch_ids, b.id]
                            : form.batch_ids.filter((x) => x !== b.id);
                          setForm({ ...form, batch_ids: next });
                        }}
                      />
                      {b.name} <span className="text-slate-400">· {b.subject}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {form.role === "parent" && (
              <div>
                <label className="text-sm font-semibold text-slate-700">Linked student</label>
                <select
                  value={form.parent_of}
                  onChange={(e) => setForm({ ...form, parent_of: e.target.value })}
                  data-testid="user-parent-of"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none"
                >
                  <option value="">— none —</option>
                  {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
                </select>
              </div>
            )}

            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" data-testid="user-submit" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">Create</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </Section>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {ROLES.map((r) => (
          <button
            key={r}
            onClick={() => setFilter(r)}
            data-testid={`filter-${r}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
              filter === r ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Batches</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">
                  {u.role === "student" ? (
                    <Link to={`/dashboard/admin/students/${u.id}`}
                      data-testid={`open-student-${u.id}`}
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                      {u.name}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : u.name}
                </td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{u.role}</span>
                  {u.is_primary_admin && (
                    <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-900 text-white" data-testid={`primary-badge-${u.id}`}>Primary</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{u.batch_ids?.length || 0}</td>
                <td className="px-4 py-3 text-right">
                  {(u.role !== "admin" || (me?.is_primary_admin && !u.is_primary_admin)) && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditUser(u)} className="text-slate-600 hover:bg-slate-100 p-1.5 rounded-lg" data-testid={`edit-user-${u.id}`} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => resetPw(u)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" data-testid={`reset-pw-${u.id}`} title="Reset password">
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button onClick={() => toggleActive(u)} className={`p-1.5 rounded-lg ${u.is_active === false ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}
                        data-testid={`toggle-active-${u.id}`} title={u.is_active === false ? "Activate" : "Deactivate"}>
                        <Power className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(u.id)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded-lg" data-testid={`delete-user-${u.id}`} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
      />
    </div>
  );
}
